import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAccounts } from "../lib/account";
import { moneyFor } from "../lib/currency";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  ComposedChart, Line, Area, AreaChart,
} from "recharts";
import PnLComparison from "../components/PnLComparison";
import TradesRangeDialog from "../components/TradesRangeDialog";
import { ArrowRight, ChartBar as ChartBarIcon, Package, ChartLineUp, Coins, TrendUp } from "@phosphor-icons/react";

// Convert a period bucket key (e.g. "2026-01", "2026-W03", "2026") into a from/to range.
function bucketRange(key, kind) {
  if (kind === "daily") {
    const [yy, mm, dd] = key.split("-").map(Number);
    const d = new Date(Date.UTC(yy, mm - 1, dd));
    return { from: key, to: key, label: d.toLocaleDateString(undefined,
      { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) };
  }
  if (kind === "weekly") {
    const m = key.match(/^(\d{4})-W(\d{2})$/);
    if (!m) return null;
    const [_, y, w] = m;
    const jan4 = new Date(Date.UTC(parseInt(y), 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;
    const monday = new Date(jan4.getTime());
    monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (parseInt(w) - 1) * 7);
    const sunday = new Date(monday.getTime());
    sunday.setUTCDate(monday.getUTCDate() + 6);
    return { from: monday.toISOString().slice(0,10), to: sunday.toISOString().slice(0,10),
             label: `Week ${w}, ${y} · ${monday.toISOString().slice(5,10)} → ${sunday.toISOString().slice(5,10)}` };
  }
  if (kind === "monthly") {
    const [y, m] = key.split("-");
    const from = `${y}-${m}-01`;
    const last = new Date(parseInt(y), parseInt(m), 0).getDate();
    const to = `${y}-${m}-${String(last).padStart(2,"0")}`;
    const name = new Date(parseInt(y), parseInt(m)-1, 1).toLocaleString(undefined, { month: "long" });
    return { from, to, label: `${name} ${y}` };
  }
  if (kind === "yearly") {
    return { from: `${key}-01-01`, to: `${key}-12-31`, label: `Year ${key}` };
  }
  return null;
}

function PeriodTable({ rows, label, kind, onRowClick, account }) {
  const hasData = rows.length > 0;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
      {/* Chart occupies 2 of 5 columns on xl, full width below */}
      <div className="xl:col-span-2 rounded-xl border border-border bg-card p-6 min-h-[340px] flex flex-col">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label} P&L</p>
        <div className="flex-1 mt-4 min-h-[260px]">
          {hasData ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="pnl" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              No {label.toLowerCase()} data yet — journal a trade to see it here.
            </div>
          )}
        </div>
      </div>
      {/* Breakdown table takes 3 of 5 columns — this is where users spend time */}
      <div className="xl:col-span-3 rounded-xl border border-border bg-card p-6">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1">{label} breakdown</p>
        <p className="text-xs text-muted-foreground mb-3">Click any row to see the trades from that period.</p>
        <div className="max-h-[420px] overflow-auto hide-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-border">
                <th className="py-2">Period</th>
                <th className="py-2 text-right">Trades</th>
                <th className="py-2 text-right">P&L</th>
                <th className="py-2 text-right">Return %</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No data</td></tr>}
              {rows.slice().reverse().map(r => (
                <tr key={r.period}
                  data-testid={`period-row-${kind}-${r.period}`}
                  onClick={() => onRowClick(r.period, kind)}
                  className="border-b border-border/60 cursor-pointer hover:bg-secondary/40 group">
                  <td className="py-2.5 font-mono-num">{r.period}</td>
                  <td className="py-2.5 text-right font-mono-num">{r.trades}</td>
                  <td className={`py-2.5 text-right font-mono-num font-bold ${r.pnl >= 0 ? "text-emerald-400" : "text-red-500"}`}>
                    {moneyFor(account, r.pnl, { sign: true })}
                  </td>
                  <td className={`py-2.5 text-right font-mono-num ${r.return_pct >= 0 ? "text-emerald-400" : "text-red-500"}`}>
                    {r.return_pct}%
                  </td>
                  <td className="py-2.5 text-right pr-1">
                    <ArrowRight size={12} className="inline text-muted-foreground group-hover:text-primary transition-colors" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const DURATIONS = [
  { id: "7",   label: "Last 7 days" },
  { id: "30",  label: "Last 30 days" },
  { id: "90",  label: "Last 90 days" },
  { id: "365", label: "Last 365 days" },
];

function ActivityKpi({ icon: Icon, label, value, sub, testid, tone }) {
  const toneCls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-500" : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card p-5 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground leading-tight">{label}</p>
        <Icon size={14} className="text-muted-foreground shrink-0" />
      </div>
      <p className={`font-mono-num text-2xl font-bold mt-2 truncate ${toneCls}`} title={String(value)}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1 truncate" title={sub}>{sub}</p>}
    </div>
  );
}

function ActivitySection({ account, activeId }) {
  const [duration, setDuration] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    api.get(`/analytics/activity?account_id=${activeId}&days=${duration}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [activeId, duration]);

  const rows = data?.rows || [];
  const hasData = rows.length > 0;
  const ccyLabel = DURATIONS.find(d => d.id === duration)?.label || "";

  return (
    <div className="mt-10" data-testid="activity-section">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">Detailed P&L Analysis</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Activity · Volume · P&L</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Window</span>
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger className="w-[180px]" data-testid="activity-duration-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DURATIONS.map(d => (
                <SelectItem key={d.id} value={d.id} data-testid={`duration-opt-${d.id}`}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs — responsive: 1 col on xs, 2 on md, 4 on xl. Prevents cramped rendering
          at ~1000px viewports where 4-across squeezed long labels like "Net P&L · Last 30 days". */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <ActivityKpi icon={Package}
          label="Closed Orders"
          value={data?.total_orders ?? 0}
          sub={`avg ${data?.avg_daily_orders ?? 0} / day · ${data?.active_days ?? 0} active days`}
          testid="kpi-total-orders" />
        <ActivityKpi icon={Coins}
          label="Trading Volume"
          value={moneyFor(account, data?.total_volume ?? 0, { short: true })}
          sub={`avg ${moneyFor(account, data?.avg_daily_volume ?? 0, { short: true })} / day`}
          testid="kpi-total-volume" />
        <ActivityKpi icon={ChartLineUp}
          label={`Net P&L · ${ccyLabel}`}
          value={moneyFor(account, data?.total_pnl ?? 0, { sign: true })}
          sub={data?.best_day ? `Best: ${data.best_day.date} · ${moneyFor(account, data.best_day.pnl, { sign: true, short: true })}` : "no data"}
          testid="kpi-window-pnl"
          tone={(data?.total_pnl ?? 0) >= 0 ? "up" : "down"} />
        <ActivityKpi icon={TrendUp}
          label="Peak Volume Day"
          value={data?.peak_volume_day ? moneyFor(account, data.peak_volume_day.volume, { short: true }) : "—"}
          sub={data?.peak_volume_day?.date || "no data"}
          testid="kpi-peak-volume" />
      </div>

      {/* Closed Orders + Trading Volume — side-by-side on xl, stacked below */}
      <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="rounded-xl border border-border bg-card p-6" data-testid="closed-orders-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1">
              <Package size={11} /> Closed Orders
            </p>
            <h3 className="font-display text-lg font-semibold mt-1">Orders closed per day</h3>
          </div>
          <p className="text-xs text-muted-foreground shrink-0">{ccyLabel}</p>
        </div>
        <div className="h-64 sm:h-72 w-full">
          {!hasData && <p className="h-full flex items-center justify-center text-sm text-muted-foreground">{loading ? "Loading…" : "No trades in this window"}</p>}
          {hasData && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => [v, "orders"]} />
                <Bar dataKey="closed_orders" name="Orders" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]}>
                  {rows.map((r, i) => (
                    <Cell key={`co-${i}`} fill={r.closed_orders >= (data?.avg_daily_orders || 0) * 1.5 ? "hsl(var(--primary))" : "hsl(var(--primary) / 0.55)"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Trading Volume Graph */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="volume-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1">
              <Coins size={11} /> Trading Volume
            </p>
            <h3 className="font-display text-lg font-semibold mt-1">Notional deployed per day</h3>
          </div>
          <p className="text-xs text-muted-foreground shrink-0">|qty × entry_price|</p>
        </div>
        <div className="h-64 sm:h-72 w-full">
          {!hasData && <p className="h-full flex items-center justify-center text-sm text-muted-foreground">{loading ? "Loading…" : "No trades in this window"}</p>}
          {hasData && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rows} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <defs>
                  <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="hsl(217 91% 60%)" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="hsl(217 91% 60%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10}
                  tickFormatter={(v) => moneyFor(account, v, { short: true })} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => moneyFor(account, v)} />
                <Area type="monotone" dataKey="volume" name="Volume" stroke="hsl(217 91% 60%)" fill="url(#volGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      </div>
      {/* /Closed Orders + Trading Volume side-by-side pair */}

      {/* Combined P&L bars over the same window */}
      <div className="rounded-xl border border-border bg-card p-6 mt-6" data-testid="window-pnl-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1">
              <ChartLineUp size={11} /> Daily P&L
            </p>
            <h3 className="font-display text-lg font-semibold mt-1">P&L over the same window</h3>
          </div>
          <p className="text-xs text-muted-foreground shrink-0">green = winning day, red = losing day</p>
        </div>
        <div className="h-64 sm:h-72 w-full">
          {!hasData && <p className="h-full flex items-center justify-center text-sm text-muted-foreground">{loading ? "Loading…" : "No trades in this window"}</p>}
          {hasData && (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10}
                  tickFormatter={(v) => moneyFor(account, v, { short: true })} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, key) => key === "closed_orders" ? [v, "orders"] : moneyFor(account, v, { sign: true })} />
                <Bar dataKey="pnl" name="P&L" radius={[3, 3, 0, 0]}>
                  {rows.map((r, i) => (
                    <Cell key={`pnl-${i}`} fill={r.pnl >= 0 ? "#10b981" : "#ef4444"} />
                  ))}
                </Bar>
                <Line type="monotone" dataKey="closed_orders" name="Orders" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} dot={false} yAxisId={0} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const { activeId, activeAccount } = useAccounts();
  const [p, setP] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [range, setRange] = useState({ from: "", to: "", title: "", subtitle: "" });

  useEffect(() => {
    if (!activeId) return;
    api.get(`/analytics/periods?account_id=${activeId}`).then(({ data }) => setP(data));
  }, [activeId]);

  const onRowClick = (key, kind) => {
    const r = bucketRange(key, kind);
    if (!r) return;
    setRange({ from: r.from, to: r.to, title: r.label,
               subtitle: `${kind.charAt(0).toUpperCase() + kind.slice(1)} · ${key}` });
    setRangeOpen(true);
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto w-full" data-testid="analytics-page">
      <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">Performance</p>
      <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight mt-2">Analytics</h1>
      <p className="text-base text-muted-foreground mt-2 mb-8">
        Baseline: {moneyFor(activeAccount, p?.starting_balance || 0)} (from account starting balance).
      </p>

      {/* Detailed period comparison — moved to top (was collapsed at bottom).
          Open by default so the first thing users see is the WoW / MoM / YoY snapshot. */}
      <div className="mb-8" data-testid="analytics-comparison-section">
        <details open className="rounded-2xl border border-border bg-card">
          <summary className="cursor-pointer list-none p-6 flex items-center justify-between" data-testid="toggle-detailed-comparison">
            <div>
              <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">Snapshot</p>
              <h3 className="font-display text-xl font-semibold mt-1">Detailed period comparison</h3>
              <p className="text-xs text-muted-foreground mt-1">Current vs previous — daily, weekly, monthly, yearly.</p>
            </div>
            <ChartBarIcon size={20} className="text-primary" />
          </summary>
          <div className="px-6 pb-6">
            <PnLComparison />
          </div>
        </details>
      </div>

      <Tabs defaultValue="monthly">
        <TabsList data-testid="period-tabs">
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="yearly" data-testid="tab-yearly">Yearly</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-6"><PeriodTable rows={p?.daily || []} label="Daily" kind="daily" onRowClick={onRowClick} account={activeAccount} /></TabsContent>
        <TabsContent value="weekly" className="mt-6"><PeriodTable rows={p?.weekly || []} label="Weekly" kind="weekly" onRowClick={onRowClick} account={activeAccount} /></TabsContent>
        <TabsContent value="monthly" className="mt-6"><PeriodTable rows={p?.monthly || []} label="Monthly" kind="monthly" onRowClick={onRowClick} account={activeAccount} /></TabsContent>
        <TabsContent value="yearly" className="mt-6"><PeriodTable rows={p?.yearly || []} label="Yearly" kind="yearly" onRowClick={onRowClick} account={activeAccount} /></TabsContent>
      </Tabs>

      <ActivitySection account={activeAccount} activeId={activeId} />

      <TradesRangeDialog open={rangeOpen} onOpenChange={setRangeOpen}
        from={range.from} to={range.to} title={range.title} subtitle={range.subtitle} />
    </div>
  );
}
